import React, { ChangeEvent, FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, Divider, FormControlLabel, Typography } from '@mui/material';
import { OrderInputType, PatientFormData } from 'models/order-input/shared';
import PatientForm from 'components/order-flow/shared/patient-form.component';
import { SupplierOrderContext } from 'pages/order-flow/supplier-order/supplier-order-context';
import SupplierGrid from 'components/order-flow/shared/supplier-grid/supplier-grid.component';
import ResponsibleUserAutocomplete from 'components/order-flow/shared/supplier-grid/responsible-user-autocomplete.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { isCustomer } from 'utils/client-settings';
import { hasRequiredPatientNames } from 'components/order-flow/shared/order-input-helper';

// DD-3494 PR 2 turns this on once the deployed backend accepts a patient-less
// createOrder for lab-to-lab orders. Until then the option stays hidden.
const ALLOW_WITHHOLDING_PATIENT_REFERENCE = false;

type PatientSupplierStepProps = {
    onNextClick: () => void;
};
const PatientSupplierStep: FC<PatientSupplierStepProps> = ({ onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, updateOrder } = useContext(SupplierOrderContext);
    const [isPatientFormValid, setIsPatientFormValid] = useState(false);
    // Only the ordering lab picks its responsible user; the supplier editing the order does not.
    const isCustomerOrganization = isCustomer(currentOrgVar()._id, order?.customer?._id as string);

    const handlePatientChange = (formData: PatientFormData, isFormValid: boolean) => {
        setIsPatientFormValid(isFormValid);
        updateOrder({
            ...order,
            patient: {
                ...order.patient,
                ...formData,
            },
        });
    };

    const handleWithholdPatientChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateOrder({ ...order, withholdPatient: event.target.checked });
    };

    return (
        <Fragment>
            {order ? (
                <Box>
                    <Box mb={2}>
                        {order.inputType === OrderInputType.NEW ? (
                            <Typography variant="h1">{translate('orders:NEW_ORDER')}</Typography>
                        ) : (
                            <Typography variant="h1">{translate('orders:UPDATE_ORDER')}</Typography>
                        )}
                    </Box>
                    <Box mb={2}>
                        <Divider />
                    </Box>
                    <Box mb={4}>
                        <Box mb={1}>
                            <Typography variant="h2">{translate('orders:PATIENT_INFORMATION')}</Typography>
                        </Box>
                        {ALLOW_WITHHOLDING_PATIENT_REFERENCE && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(order.withholdPatient)}
                                        onChange={handleWithholdPatientChange}
                                        data-test="patient-supplier-step.withhold-patient-checkbox"
                                    />
                                }
                                label={translate('orders:DO_NOT_SEND_PATIENT_REFERENCE')}
                            />
                        )}
                        {!order.withholdPatient && (
                            <PatientForm
                                isEdit={order.inputType !== OrderInputType.NEW}
                                patient={order.patient}
                                onPatientChange={handlePatientChange}
                            />
                        )}
                    </Box>
                    {isCustomerOrganization ? (
                        <Box mb={4}>
                            <Box mb={1}>
                                <Typography variant="h2">{translate('orders:RESPONSIBLE_USER')}</Typography>
                            </Box>
                            <Box maxWidth={360}>
                                <ResponsibleUserAutocomplete />
                            </Box>
                        </Box>
                    ) : undefined}
                    <SupplierGrid isEdit={order.inputType !== OrderInputType.NEW} initialValue={order.supplier} />
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            disabled={
                                (!order.withholdPatient &&
                                    (!isPatientFormValid || !hasRequiredPatientNames(order.patient))) ||
                                !order.supplier?._id ||
                                !order.dentist?._id
                            }
                            onClick={() => onNextClick()}
                        >
                            {translate('common:NEXT_STEP')}
                        </Button>
                    </Box>
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default PatientSupplierStep;
